import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AmigosChat, AmigosPicker, AmigosTopics } from "@/components/AmigosChat";
import { Loader2, Mic, Sparkles, ShieldCheck } from "lucide-react";

const STEPS = [
  ["1", "Elegí un amigo", "Perezoso, tucána, ranita o mono: cada uno tiene su forma de hablar."],
  ["2", "Hablá en voz alta", "Tocás el micrófono y contestás. Tu amigo escucha, responde y corrige con cariño."],
  ["3", "Ganá estrellas", "Cada conversación suma estrellas, racha diaria y palabras nuevas."],
];

/** Public landing page: talk out loud in Spanish with the Costa Rican animal friends. */
export default function Amigos() {
  const [characters, setCharacters] = useState([]);
  const [picked, setPicked] = useState(null);
  const [topicId, setTopicId] = useState(null);
  const [open, setOpen] = useState(null);   // null = loading, false = not public yet

  useEffect(() => {
    api.get("/amigos/settings")
      .then((r) => {
        setOpen(!!r.data.public_enabled || !!r.data.allowed);
        if (r.data.public_enabled || r.data.allowed) {
          api.get("/amigos/characters").then((c) => setCharacters(c.data || []));
        }
      })
      .catch(() => setOpen(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#fffdf9]">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(0,74,173,0.10),transparent_55%),radial-gradient(circle_at_85%_10%,rgba(255,115,29,0.14),transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-10 sm:pt-24">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#ff731d]">
            We Speak Spanish · Amigos que hablan
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#004aad] mt-4 max-w-3xl">
            Practicá español hablando con tus amigos ticos
          </h1>
          <p className="text-base md:text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">
            Cuatro animales de Costa Rica te escuchan de verdad: hablás en voz alta, te responden hablando,
            te corrigen con cariño y te van enseñando palabras nuevas. Sin miedo a equivocarse.
          </p>
          <div className="flex flex-wrap gap-5 mt-7 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2"><Mic className="h-4 w-4 text-[#ff731d]" /> Conversación por voz</span>
            <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> Estrellas y racha diaria</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Espacio seguro, sin notas</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pb-20">
        {open === false ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center" data-testid="amigos-soon">
            <p className="font-display text-2xl font-bold text-[#004aad]">¡Muy pronto!</p>
            <p className="text-sm text-slate-600 mt-3 max-w-lg mx-auto">
              Estamos afinando a los amigos para que te escuchen bien. Mientras tanto, agendá una
              reunión gratis y te contamos todo.
            </p>
            <a href="/reservar/reuniones" data-testid="amigos-soon-cta"
               className="inline-flex items-center justify-center min-h-[48px] rounded-full bg-[#ff731d] px-8 mt-6 font-semibold text-white hover:bg-[#e5651a] transition-colors">
              Agendar una reunión gratis
            </a>
          </div>
        ) : picked && topicId ? (
          <AmigosChat character={picked} topicId={topicId}
                      onBack={() => { setTopicId(null); setPicked(null); }} />
        ) : picked ? (
          <AmigosTopics character={picked} onStart={setTopicId} onBack={() => setPicked(null)} />
        ) : characters.length === 0 ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#004aad]" /></div>
        ) : (
          <>
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-5">
              Elegí con quién querés conversar hoy
            </h2>
            <AmigosPicker characters={characters} onPick={setPicked} />

            <div className="grid gap-6 sm:grid-cols-3 mt-16">
              {STEPS.map(([n, title, body]) => (
                <div key={n} className="rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="inline-grid place-items-center h-9 w-9 rounded-full bg-[#004aad] text-white font-bold">{n}</span>
                  <p className="font-display text-lg font-bold text-slate-900 mt-4">{title}</p>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 rounded-3xl bg-[#004aad] text-white p-8 sm:p-12" data-testid="amigos-cta">
              <h2 className="font-display text-2xl sm:text-3xl font-bold">
                ¿Querés practicar sin límite y con profe de verdad?
              </h2>
              <p className="text-white/80 mt-3 max-w-2xl">
                Los amigos son para practicar cuando querás. En We Speak Spanish tenés clases con
                profesores ticos, en línea o presenciales, con tu propio plan.
              </p>
              <a href="/reservar/reuniones"
                 className="inline-flex items-center justify-center min-h-[48px] rounded-full bg-[#ff731d] px-8 mt-6 font-semibold hover:bg-[#e5651a] transition-colors"
                 data-testid="amigos-cta-btn">
                Agendar una reunión gratis
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
